import { toast } from '@tempo-io/sui-sonner';
import { translateDynamicKey } from '@tempo-io/translations';
import { createContext, type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

type SnackbarState = {
  message: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
};

type SnackbarContextType = {
  setMessage: (state: SnackbarState | undefined) => void;
  onGlobalError: (error: unknown, variables: unknown) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  setMessage: () => {
    return;
  },
  onGlobalError: () => {
    return;
  },
});

type SnackbarProviderProps = {
  children: ReactNode;
};

export const useSetSnackbarGlobalError = () => useContext(SnackbarContext).onGlobalError;
export const useSetSnackbarMessage = () => useContext(SnackbarContext).setMessage;

const showToast = (state: SnackbarState | undefined) => {
  if (!state) return;
  const { message, severity = 'info' } = state;
  if (severity === 'error') toast.error(message, { id: message, duration: 6000 });
  else if (severity === 'warning') toast.warning(message, { id: message, duration: 6000 });
  else if (severity === 'success') toast.success(message, { id: message, duration: 6000 });
  else toast(message, { id: message, duration: 6000 });
};

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const { t } = useTranslation('translation');

  const handleGlobalError = (error: unknown) => {
    const responseData: Record<string, unknown> =
      (error as { response?: { data?: Record<string, unknown> } })?.response?.data ?? {};
    const message = (responseData.message as string) || '';

    let key: string;
    if (translateDynamicKey(t, `intake.serverErrors.${message}`, '')) {
      key = `intake.serverErrors.${message}`;
    } else if (translateDynamicKey(t, message, '')) {
      key = message;
    } else {
      key = 'intake.serverErrors.generic';
    }
    showToast({
      message: translateDynamicKey(t, key, message, responseData),
      severity: 'error',
    });
  };

  const context = {
    setMessage: showToast,
    onGlobalError: handleGlobalError,
  };

  return <SnackbarContext.Provider value={context}>{children}</SnackbarContext.Provider>;
};
